import {
  Alert,
  Button,
  Checkbox,
  Group,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { barve } from '../tema';

/** Šifranta iz Supermana: vrste vozil in klavzule (kot VrstaVozilaEditor in KlavzulaEditor: tabela + obrazec). */
export function Sifranti() {
  const [sifrant, setSifrant] = useState<'vrste' | 'klavzule'>('vrste');
  return (
    <Stack gap="md">
      <SegmentedControl
        value={sifrant}
        onChange={(v) => setSifrant(v as 'vrste' | 'klavzule')}
        data={[
          { value: 'vrste', label: 'Vrste vozil' },
          { value: 'klavzule', label: 'Klavzule' },
        ]}
        w={{ base: '100%', xs: 'auto' }}
        style={{ alignSelf: 'flex-start' }}
      />
      {sifrant === 'vrste' ? <VrsteVozil /> : <Klavzule />}
    </Stack>
  );
}

function VrsteVozil() {
  const odjemalec = useQueryClient();
  const seznam = useQuery({
    queryKey: ['vrste-vozil'],
    queryFn: ({ signal }) => podatki(api.GET('/api/vrste-vozil', { signal })),
  });
  const [izbrana, setIzbrana] = useState<Shema['SifrantPodatki']>({ naziv: '' });
  const shrani = async () => {
    const telo = { ...izbrana, naziv: (izbrana.naziv ?? '').trim() };
    const v = izbrana.id
      ? await podatki(api.PUT('/api/vrste-vozil/{id}', { params: { path: { id: izbrana.id } }, body: telo }))
      : await podatki(api.POST('/api/vrste-vozil', { body: telo }));
    await odjemalec.invalidateQueries({ queryKey: ['vrste-vozil'] });
    setIzbrana(v);
  };
  if (!seznam.data) {
    return (
      <StanjeNalaganja
        napaka={seznam.error}
        ponovi={() => void seznam.refetch()}
        naslov="Vrst vozil ni bilo mogoče naložiti"
      />
    );
  }
  return (
    <Urejanje
      naslov="Vrste vozil"
      vrstice={seznam.data.map((v) => ({ id: v.id!, besedilo: v.naziv ?? '' }))}
      izbranaId={izbrana.id}
      izberi={(id) => setIzbrana(seznam.data.find((v) => v.id === id) ?? { naziv: '' })}
      nova={() => setIzbrana({ naziv: '' })}
      shrani={shrani}
      obrazec={(napake) => (
        <TextInput
          label="Vrsta vozila"
          value={izbrana.naziv ?? ''}
          onChange={(e) => setIzbrana({ ...izbrana, naziv: e.currentTarget.value.toUpperCase() })}
          error={napake.naziv}
          placeholder="npr. OSEBNO VOZILO"
        />
      )}
    />
  );
}

function Klavzule() {
  const odjemalec = useQueryClient();
  const seznam = useQuery({
    queryKey: ['klavzule'],
    queryFn: ({ signal }) => podatki(api.GET('/api/klavzule', { signal })),
  });
  const [izbrana, setIzbrana] = useState<Shema['KlavzulaPodatki']>({ besedilo: '', dodajZaVozilo: false });
  const shrani = async () => {
    const telo = { ...izbrana, besedilo: (izbrana.besedilo ?? '').trim() };
    const k = izbrana.id
      ? await podatki(api.PUT('/api/klavzule/{id}', { params: { path: { id: izbrana.id } }, body: telo }))
      : await podatki(api.POST('/api/klavzule', { body: telo }));
    await odjemalec.invalidateQueries({ queryKey: ['klavzule'] });
    setIzbrana(k);
  };
  if (!seznam.data) {
    return (
      <StanjeNalaganja
        napaka={seznam.error}
        ponovi={() => void seznam.refetch()}
        naslov="Klavzul ni bilo mogoče naložiti"
      />
    );
  }
  return (
    <Urejanje
      naslov="Klavzule"
      vrstice={seznam.data.map((k) => ({
        id: k.id!,
        besedilo: k.besedilo ?? '',
        dodatek: k.dodajZaVozilo ? 'za vozilo' : undefined,
      }))}
      izbranaId={izbrana.id}
      izberi={(id) => setIzbrana(seznam.data.find((k) => k.id === id) ?? { besedilo: '' })}
      nova={() => setIzbrana({ besedilo: '', dodajZaVozilo: false })}
      shrani={shrani}
      obrazec={(napake) => (
        <Stack gap="xs">
          <Textarea
            label="Besedilo klavzule"
            value={izbrana.besedilo ?? ''}
            onChange={(e) => setIzbrana({ ...izbrana, besedilo: e.currentTarget.value })}
            error={napake.besedilo}
            autosize
            minRows={3}
            maxRows={10}
          />
          <Checkbox
            label="Dodaj ob vozilu (na račun/predračun z vozilom se doda samodejno)"
            checked={!!izbrana.dodajZaVozilo}
            onChange={(e) => setIzbrana({ ...izbrana, dodajZaVozilo: e.currentTarget.checked })}
          />
        </Stack>
      )}
    />
  );
}

/** Tabela šifranta in obrazec za izbrano ali novo postavko (kot SimpleAbstractEditor). */
function Urejanje({
  naslov,
  vrstice,
  izbranaId,
  izberi,
  nova,
  shrani,
  obrazec,
}: {
  naslov: string;
  vrstice: { id: number; besedilo: string; dodatek?: string }[];
  izbranaId?: number;
  izberi: (id: number) => void;
  nova: () => void;
  shrani: () => Promise<void>;
  obrazec: (napake: Record<string, string>) => React.ReactNode;
}) {
  const [napaka, setNapaka] = useState<string | null>(null);
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [dela, setDela] = useState(false);
  const potrdi = async () => {
    setNapaka(null);
    setPolja({});
    setDela(true);
    try {
      await shrani();
      notifications.show({ color: 'green', message: 'Shranjeno.' });
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };
  return (
    <Stack gap="md">
      <Kartica
        naslov={izbranaId ? 'Uredi' : 'Nova postavka'}
        dejanja={
          izbranaId ? (
            <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={nova}>
              Nova
            </Button>
          ) : undefined
        }
      >
        {obrazec(polja)}
        {napaka && (
          <Alert color="red" variant="light" mt="sm" role="alert">
            {napaka}
          </Alert>
        )}
        <Group justify="flex-end" mt="md">
          <Button onClick={() => void potrdi()} loading={dela}>
            {izbranaId ? 'Shrani' : 'Dodaj'}
          </Button>
        </Group>
      </Kartica>
      <Kartica naslov={`${naslov} (${vrstice.length})`}>
        {vrstice.length === 0 ? (
          <Text size="sm" c="dimmed">
            Šifrant je prazen.
          </Text>
        ) : (
          <Table highlightOnHover verticalSpacing={6}>
            <Table.Tbody>
              {vrstice.map((v) => (
                <Table.Tr
                  key={v.id}
                  onClick={() => izberi(v.id)}
                  style={{ cursor: 'pointer', background: v.id === izbranaId ? barve.izbrano : undefined }}
                  aria-selected={v.id === izbranaId}
                >
                  <Table.Td style={{ whiteSpace: 'pre-wrap' }}>
                    {v.besedilo}
                    {v.dodatek && (
                      <Text span size="xs" c="dimmed">
                        {' '}
                        · {v.dodatek}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Kartica>
    </Stack>
  );
}
